'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import { PreferenceGroupConfig } from './config';

interface PreferenceGroupProps {
  group: PreferenceGroupConfig;
  value: string;
  onChange: (value: string) => void;
}

/**
 * Renders a single preference group. "cards" variant shows richer option cards
 * (icon + title + description). "segmented" shows compact selectable pills.
 */
export default function PreferenceGroup({ group, value, onChange }: PreferenceGroupProps) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon name={group.icon} size={13} variant="outline" className="text-gray-500" />
        <span className="text-xs font-semibold text-gray-300">{group.label}</span>
      </div>

      {group.variant === 'cards' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2.5">
          {group.options.map((opt) => {
            const active = value === opt.value;
            return (
              <motion.button
                key={opt.value}
                type="button"
                whileTap={{ scale: 0.97 }}
                onClick={() => onChange(opt.value)}
                className={`relative text-left rounded-xl p-3 border transition-colors duration-200 ${
                  active
                    ? 'border-purple-500/60 bg-purple-500/[0.08]'
                    : 'border-white/[0.08] bg-white/[0.02] hover:border-white/20'
                }`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div
                    className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                      active ? 'bg-purple-500/20 text-purple-200' : 'bg-white/[0.05] text-gray-400'
                    }`}
                  >
                    <Icon name={opt.icon || 'SparklesIcon'} size={15} variant="outline" />
                  </div>
                  <motion.span
                    initial={false}
                    animate={{ scale: active ? 1 : 0, opacity: active ? 1 : 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 22 }}
                    className="w-4 h-4 rounded-full bg-purple-500 flex items-center justify-center"
                  >
                    <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </motion.span>
                </div>
                <p className={`text-xs font-bold leading-tight ${active ? 'text-white' : 'text-gray-200'}`}>
                  {opt.label}
                </p>
                {opt.description && (
                  <p className="text-[11px] text-gray-500 leading-snug mt-0.5">{opt.description}</p>
                )}
              </motion.button>
            );
          })}
        </div>
      ) : (
        <div className="inline-flex flex-wrap gap-1 p-0.5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
          {group.options.map((opt) => {
            const active = value === opt.value;
            return (
              <motion.button
                key={opt.value}
                type="button"
                whileTap={{ scale: 0.96 }}
                onClick={() => onChange(opt.value)}
                title={opt.description}
                className={`relative px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                  active ? 'text-white' : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                {active && (
                  <motion.span
                    layoutId={`seg-${group.key}`}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    className="absolute inset-0 rounded-md bg-gradient-to-br from-purple-600 to-purple-500 shadow-sm shadow-purple-500/40"
                  />
                )}
                <span className="relative z-10 flex items-center gap-1">
                  {opt.label}
                  {opt.requiresReference && (
                    <span className="text-[9px] uppercase tracking-wide opacity-70">· ref</span>
                  )}
                </span>
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
}
